import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { fetchProperties, updateProperty, deleteProperty, createProperty } from '../utils/api';
import { PropertySidebar } from '../compo/card';
import ButtonListFunction from '../compo/button_list_function';
import AddCard from '../compo/add_card';
import PdfUploadTest from '../compo/pdf_upload_test';
import SearchAddress from '../compo/search_address';
import { applyFilters } from '../utils/propertyFilters';
import { generatePropertyColumns } from '../utils/propertyFields';
import * as XLSX from 'xlsx';
import { geocodeAddress } from '../utils/propertyGeo';
import { useAuth, ROLES } from '../contexts/AuthContext';
import DemoFeatureInfoModal from '../compo/demo_feature_info_modal';
import { formatFloorForDisplay } from '../utils/formatFloor';

const MIN_LIST_HEIGHT = 360;

function MainList({ filters = {}, consultations = [], availableHeight, onFocusMap, onAddInterestedProperty }) {
  const { role, loading: authLoading, isAuthenticated } = useAuth();
  const isGuest = role === ROLES.GUEST;
  const isVisitor = role === ROLES.VISITOR;
  const isDemo = role === ROLES.DEMO;
  
  const [propertyData, setPropertyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingData, setEditingData] = useState({}); // { propertyId: { field: value } }
  const [isPdfUploadOpen, setIsPdfUploadOpen] = useState(false);
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isExcelUploading, setIsExcelUploading] = useState(false);
  const [excelUploadProgress, setExcelUploadProgress] = useState({ current: 0, total: 0 });
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const [addressEditTarget, setAddressEditTarget] = useState(null); // { propertyId, cell }
  const [selectedForDelete, setSelectedForDelete] = useState(new Set()); // 삭제 선택된 매물 ID
  const [isDeleting, setIsDeleting] = useState(false);
  const [demoInfoModal, setDemoInfoModal] = useState({ isOpen: false, featureName: '', description: '', steps: [] });
  
  // 무한 스크롤 관련
  const PAGE_SIZE = 50;
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const loaderRef = useRef(null);

  // 컬럼 너비 조절 관련
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const resizeColumnKey = useRef(null);

  // 드래그 감지용 ref
  const dragStartPos = useRef(null);
  const isDragging = useRef(false);

  const loadProperties = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchProperties(role); // 역할 정보 전달
      setPropertyData(data);
    } catch (err) {
      console.error('매물 데이터 로드 실패:', err);
      setError(err.message || '매물 데이터를 불러오는데 실패했습니다.');
      setPropertyData([]);
    } finally {
      setIsLoading(false);
    }
  }, [role]);

  // 백엔드에서 매물 데이터 가져오기 (로그인한 경우에만)
  useEffect(() => {
    // 인증 상태 확인 중이면 대기
    if (authLoading) {
      return;
    }
    
    // 로그인하지 않았거나 visitor면 매물 로드하지 않음
    if (!isAuthenticated || isVisitor) {
      setIsLoading(false);
      setPropertyData([]);
      return;
    }
    
    loadProperties();
  }, [authLoading, isAuthenticated, isVisitor, loadProperties]);

  // focusProperty 이벤트 처리 (지도에서 좌표 없는 매물 클릭 시)
  useEffect(() => {
    const handleFocusProperty = (event) => {
      const { propertyId } = event.detail;
      if (propertyId && propertyData.length > 0) {
        // 해당 매물 찾기
        const property = propertyData.find(p => p.id === propertyId);
        if (property) {
          // 매물 선택 및 사이드바 열기
          setSelectedProperty(property);
          setIsSidebarOpen(true);
          // 테이블에서 해당 행으로 스크롤 (약간의 지연 후)
          setTimeout(() => {
            const row = document.querySelector(`[data-property-id="${propertyId}"]`);
            if (row) {
              row.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // 행 하이라이트
              row.classList.add('bg-yellow-100');
              setTimeout(() => {
                row.classList.remove('bg-yellow-100');
              }, 2000);
            }
          }, 300);
        }
      }
    };

    window.addEventListener('focusProperty', handleFocusProperty);
    return () => {
      window.removeEventListener('focusProperty', handleFocusProperty);
    };
  }, [propertyData]);

  // 편집 모드 시작
  const handleEditClick = () => {
    setIsEditing(true);
    setEditingData({});
    setSelectedForDelete(new Set());
  };

  // 편집 모드 종료 및 저장
  const handleSaveClick = async () => {
    if (Object.keys(editingData).length === 0) {
      setIsEditing(false);
      setSelectedForDelete(new Set());
      return;
    }

    setIsSaving(true);
    try {
      // 각 매물별로 업데이트
      const updatePromises = Object.entries(editingData).map(async ([propertyId, changes]) => {
        const property = propertyData.find(p => p.id === parseInt(propertyId));
        if (!property) return;

        const updatedData = { ...property, ...changes };
        return await updateProperty(propertyId, updatedData);
      });

      await Promise.all(updatePromises);

      // 데이터 다시 불러오기
      const data = await fetchProperties(role); // 역할 정보 전달
      setPropertyData(data);
      setEditingData({});
      setIsEditing(false);
      setSelectedForDelete(new Set());
    } catch (err) {
      console.error('매물 업데이트 실패:', err);
      setError('매물 업데이트에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // 체크박스 토글
  const handleCheckboxToggle = (propertyId) => {
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedForDelete.size === filteredProperties.length) {
      setSelectedForDelete(new Set());
    } else {
      setSelectedForDelete(new Set(filteredProperties.map(p => p.id)));
    }
  };

  // 선택 삭제
  const handleDeleteSelected = async () => {
    if (selectedForDelete.size === 0) return;

    const confirmed = window.confirm(
      `선택한 ${selectedForDelete.size}개의 매물을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`
    );

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedForDelete).map(id => deleteProperty(id));
      await Promise.all(deletePromises);

      // 데이터 다시 불러오기
      await loadProperties();
      setSelectedForDelete(new Set());
      alert(`${selectedForDelete.size}개의 매물이 삭제되었습니다.`);
    } catch (err) {
      console.error('매물 삭제 실패:', err);
      setError('일부 매물 삭제에 실패했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredProperties = useMemo(
    () => {
      const filtered = applyFilters(propertyData, filters, consultations);
      // ID 기준으로 오름차순 정렬
      return filtered.sort((a, b) => {
        const idA = parseInt(a.id) || 0;
        const idB = parseInt(b.id) || 0;
        return idA - idB;
      });
    },
    [propertyData, filters, consultations]
  );

  // 무한 스크롤: 화면에 표시할 데이터
  const displayedProperties = useMemo(
    () => filteredProperties.slice(0, displayCount),
    [filteredProperties, displayCount]
  );

  // 더 로드할 데이터가 있는지
  const hasMore = displayCount < filteredProperties.length;

  // 필터 변경 시 displayCount 리셋
  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
  }, [filters]);

  // 무한 스크롤: Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setDisplayCount(prev => Math.min(prev + PAGE_SIZE, filteredProperties.length));
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, filteredProperties.length]);

  // 동적으로 컬럼 생성 (기존 데이터에서 필드 추출)
  // guest일 때는 특정 필드 제외
  const PROPERTY_COLUMNS = useMemo(() => {
    const columns = generatePropertyColumns(propertyData);
    
    // 내부적으로 필요한 필드 숨기기 (모든 사용자에게 적용)
    const internalFields = ['pdfTextHash', 'pdfExtracted상호', 'pdfExtracted주소', 'dataType'];
    let filteredColumns = columns.filter(col => !internalFields.includes(col.key));
    
    if (isGuest) {
      // GUEST는 번호(id), 상권, 월세, 보증금, 권리금, 평수, 층수만 표시
      const allowedFields = ['id', '상권', '월세', '보증금', '권리금', '평수', '층수(해당/전체)', '현층/총층수'];
      filteredColumns = filteredColumns.filter(col => allowedFields.includes(col.key));
    }
    return filteredColumns;
  }, [propertyData, isGuest]);

  // 컬럼 너비 초기값 로드 (로컬 스토리지에서)
  useEffect(() => {
    const savedWidths = localStorage.getItem('propertyColumnWidths');
    if (savedWidths) {
      try {
        setColumnWidths(JSON.parse(savedWidths));
      } catch (error) {
        console.error('컬럼 너비 로드 실패:', error);
      }
    }
  }, []);

  // 컬럼 너비 저장 (로컬 스토리지에)
  useEffect(() => {
    if (Object.keys(columnWidths).length > 0) {
      localStorage.setItem('propertyColumnWidths', JSON.stringify(columnWidths));
    }
  }, [columnWidths]);

  // 컬럼 너비 조절 시작
  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnKey);
    resizeStartX.current = e.clientX;
    resizeColumnKey.current = columnKey;
    
    // 현재 컬럼 너비 가져오기
    const th = e.currentTarget.closest('th');
    if (th) {
      resizeStartWidth.current = th.offsetWidth;
    } else {
      resizeStartWidth.current = columnWidths[columnKey] || 150; // 기본값 150px
    }
  }, [columnWidths]);

  // 전역 마우스 이벤트 리스너
  useEffect(() => {
    if (resizingColumn) {
      const handleMove = (e) => {
        if (resizeColumnKey.current === null) return;
        
        const diff = e.clientX - resizeStartX.current;
        const newWidth = Math.max(50, resizeStartWidth.current + diff); // 최소 50px
        
        setColumnWidths(prev => ({
          ...prev,
          [resizeColumnKey.current]: newWidth
        }));
      };
      
      const handleEnd = () => {
        setResizingColumn(null);
        resizeColumnKey.current = null;
      };
      
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizingColumn]);

  // 매물 행 클릭 시 상세 정보 표시 (편집 모드가 아닐 때만)
  const handleRowClick = (property, e) => {
    // 텍스트가 선택되어 있으면 클릭 이벤트 무시 (드래그로 간주)
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    
    // 마우스 이동 거리가 5px 이상이면 드래그로 간주
    if (isDragging.current) {
      return;
    }
    
    if (!isEditing) {
      setSelectedProperty(property);
      setIsSidebarOpen(true);
    }
  };

  // 드래그 시작 감지
  const handleRowMouseDown = (e) => {
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
    isDragging.current = false;
  };

  // 드래그 중 감지
  const handleRowMouseMove = (e) => {
    if (dragStartPos.current) {
      const deltaX = Math.abs(e.clientX - dragStartPos.current.x);
      const deltaY = Math.abs(e.clientY - dragStartPos.current.y);
      // 5px 이상 이동하면 드래그로 간주
      if (deltaX > 5 || deltaY > 5) {
        isDragging.current = true;
      }
    }
  };

  // 마우스 떼기 시 드래그 상태 초기화
  const handleRowMouseUp = () => {
    // mouseup 후 약간의 지연을 두고 상태 초기화 (onClick 이벤트가 먼저 처리되도록)
    setTimeout(() => {
      dragStartPos.current = null;
      isDragging.current = false;
    }, 50);
  };

  // 셀 클릭 시 편집 모드로 전환
  const handleCellClick = (e, property, column) => {
    if (!isEditing) return;
    e.stopPropagation();
    
    const propertyId = property.id.toString();
    const currentValue = property[column.key];
    const cell = e.currentTarget;

    // 주소 필드는 팝업으로 처리
    if (column.key === '주소') {
      setAddressEditTarget({ propertyId, cell, property });
      setIsAddressSearchOpen(true);
      return;
    }
    
    // 입력 필드로 변경
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentValue || '';
    input.className = 'w-full px-2 py-1 border border-orange-400 rounded focus:outline-none focus:ring-2 focus:ring-orange-500';
    input.style.wordBreak = 'keep-all';
    input.style.overflowWrap = 'break-word';
    
    cell.innerHTML = '';
    cell.appendChild(input);
    input.focus();
    input.select();

    const handleBlur = () => {
      const newValue = input.value.trim();
      const oldValue = property[column.key];
      
      if (newValue !== oldValue) {
        setEditingData(prev => ({
          ...prev,
          [propertyId]: {
            ...prev[propertyId],
            [column.key]: newValue === '' ? null : newValue
          }
        }));
      }
      
      // 포맷 함수가 있으면 포맷된 값으로 표시, 없으면 원본 값
      const displayValue = column.format 
        ? column.format(newValue === '' ? null : newValue)
        : (newValue || '-');
      cell.textContent = displayValue;
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        input.blur();
      } else if (e.key === 'Escape') {
        const displayValue = column.format 
          ? column.format(property[column.key])
          : (property[column.key] || '-');
        cell.textContent = displayValue;
      }
    };

    input.addEventListener('blur', handleBlur);
    input.addEventListener('keydown', handleKeyDown);
  };

  // 주소 선택 핸들러
  const handleAddressSelect = async (address) => {
    if (!addressEditTarget) return;
    
    const { propertyId, cell } = addressEditTarget;
    
    // editingData에 주소 저장
    setEditingData(prev => ({
      ...prev,
      [propertyId]: {
        ...prev[propertyId],
        주소: address
      }
    }));

    // 좌표도 함께 업데이트
    try {
      const coords = await geocodeAddress(address);
      if (coords) {
        setEditingData(prev => ({
          ...prev,
          [propertyId]: {
            ...prev[propertyId],
            주소: address,
            lat: coords.lat,
            lng: coords.lng
          }
        }));
      }
    } catch (error) {
      console.warn('주소 지오코딩 실패:', error);
    }

    // 셀 표시 업데이트
    if (cell) {
      cell.textContent = address || '-';
    }

    setIsAddressSearchOpen(false);
    setAddressEditTarget(null);
  };

  // 셀 값 가져오기
  const getCellValue = useCallback((property, column) => {
    const propertyId = property.id.toString();
    const editedValue = editingData[propertyId]?.[column.key];
    const value = editedValue !== undefined ? editedValue : property[column.key];
    
    // 층수 필드는 표시용 포맷팅 적용
    if (column.key === '층수(해당/전체)' || column.key === '현층/총층수') {
      return formatFloorForDisplay(value || '');
    }
    
    if (column.format) {
      return column.format(value);
    }
    return value || '-';
  }, [editingData]);

  const handleExportClick = useCallback(() => {
    if (filteredProperties.length === 0) {
      return;
    }

    // 헤더 행 생성
    const headers = PROPERTY_COLUMNS.map((column) => column.label);
    
    // 데이터 행 생성
    const rows = filteredProperties.map((property) =>
      PROPERTY_COLUMNS.map((column) => {
        const rawValue = getCellValue(property, column);
        return rawValue === null || rawValue === undefined ? '' : String(rawValue);
      })
    );

    // 워크북 생성
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    
    // 컬럼 너비 자동 조정
    const columnWidths = PROPERTY_COLUMNS.map(() => ({ wch: 15 }));
    worksheet['!cols'] = columnWidths;

    // 워크북 생성 및 워크시트 추가
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '매물목록');

    // Excel 파일 생성 및 다운로드
    const fileName = `매물목록_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [filteredProperties, getCellValue, PROPERTY_COLUMNS]);

  // 엑셀 업로드 처리
  const handleExcelUpload = useCallback(async (file) => {
    if (!file) return;

    setIsExcelUploading(true);
    setExcelUploadProgress({ current: 0, total: 0 });

    try {
      // 엑셀 파일 읽기
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // JSON으로 변환 (첫 번째 행을 헤더로 사용)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length < 2) {
        throw new Error('엑셀 파일에 데이터가 없습니다. (헤더 포함 최소 2행 필요)');
      }

      // 헤더 추출 (첫 번째 행)
      const headers = jsonData[0].map(h => String(h || '').trim());
      
      // 헤더를 키로 매핑 (컬럼의 label과 매칭)
      const headerToKeyMap = {};
      PROPERTY_COLUMNS.forEach(column => {
        const headerIndex = headers.findIndex(h => h === column.label);
        if (headerIndex >= 0) {
          headerToKeyMap[headerIndex] = column.key;
        }
      });

      // 데이터 행 처리 (두 번째 행부터)
      const properties = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.every(cell => !cell || String(cell).trim() === '')) {
          continue; // 빈 행 건너뛰기
        }

        const property = {};
        Object.keys(headerToKeyMap).forEach(headerIndex => {
          const key = headerToKeyMap[headerIndex];
          const value = row[headerIndex];
          if (value !== undefined && value !== null && String(value).trim() !== '') {
            property[key] = String(value).trim();
          }
        });

        if (Object.keys(property).length > 0) {
          properties.push(property);
        }
      }

      if (properties.length === 0) {
        throw new Error('엑셀 파일에서 유효한 매물 데이터를 찾을 수 없습니다.');
      }

      setExcelUploadProgress({ current: 0, total: properties.length });

      // 매물 생성 (순차적으로 처리하여 진행 상황 표시)
      const results = [];
      const errors = [];

      for (let i = 0; i < properties.length; i++) {
        try {
          setExcelUploadProgress({ current: i, total: properties.length });
          const created = await createProperty(properties[i], role); // 역할 정보 전달
          results.push(created);
        } catch (error) {
          console.error(`매물 ${i + 1} 생성 실패:`, error);
          errors.push({ index: i + 1, property: properties[i], error: error.message });
        }
      }

      setExcelUploadProgress({ current: properties.length, total: properties.length });

      // 결과 표시
      if (errors.length > 0) {
        alert(`업로드 완료: ${results.length}개 성공, ${errors.length}개 실패\n\n실패한 항목:\n${errors.map(e => `행 ${e.index}: ${e.error}`).join('\n')}`);
      } else {
        alert(`업로드 완료: ${results.length}개의 매물이 성공적으로 추가되었습니다.`);
      }

      // 데이터 새로고침
      await loadProperties();
      setIsExcelModalOpen(false);
    } catch (error) {
      console.error('엑셀 업로드 오류:', error);
      alert(`엑셀 업로드 실패: ${error.message}`);
    } finally {
      setIsExcelUploading(false);
      setExcelUploadProgress({ current: 0, total: 0 });
    }
  }, [PROPERTY_COLUMNS, loadProperties, role]);


  const containerStyle = availableHeight
    ? { height: `${availableHeight}px`, minHeight: `${MIN_LIST_HEIGHT}px` }
    : { minHeight: `${MIN_LIST_HEIGHT}px` };

  const handleSidebarReset = useCallback(() => {
    setIsSidebarOpen(false);
    setSelectedProperty(null);
  }, []);

  const quickActions = useMemo(() => {
    // GUEST는 퀵메뉴 전체 숨김
    if (isGuest) {
      return [];
    }

    const actions = [];

    if (!isEditing) {
      actions.push({
        key: 'edit',
        label: isSaving ? '💾' : '✏️',
        title: isSaving ? '저장 중' : '편집',
        color: 'bg-orange-500 hover:bg-orange-600',
        onClick: handleEditClick,
        disabled: isSaving
      });
    }

    if (!isEditing) {
      actions.push({
        key: 'add',
        label: '➕',
        title: '매물 추가',
        color: 'bg-purple-500 hover:bg-purple-600',
        onClick: () => setIsAddCardOpen(true),
        disabled: isSaving
      });
    }

    actions.push({
      key: 'excel',
      label: '📊',
      title: '엑셀',
      color: 'bg-blue-500 hover:bg-blue-600',
      onClick: () => setIsExcelModalOpen(true),
      disabled: false
    });


    if (!isEditing) {
      actions.push({
        key: 'pdf-upload',
        label: '📄',
        title: 'PDF 업로드',
        color: 'bg-green-500 hover:bg-green-600',
        onClick: () => {
          if (isDemo) {
            setDemoInfoModal({
              isOpen: true,
              featureName: 'PDF 업로드',
              description: 'PDF 파일을 업로드하면 AI가 자동으로 매물 정보를 추출하고 정형화하여 데이터베이스에 저장합니다.',
              steps: [
                'PDF 업로드 버튼을 클릭합니다.',
                '업로드할 PDF 파일들을 선택합니다 (여러 개 선택 가능).',
                '각 파일에 해당하는 상권을 지정합니다.',
                '분석 및 확인 버튼을 클릭하면 AI가 PDF 내용을 분석합니다.',
                '분석 결과를 확인하고 승인하면 매물이 자동으로 등록됩니다.',
                '기존 매물과 중복되거나 변경사항이 있으면 자동으로 감지됩니다.'
              ]
            });
          } else {
            setIsPdfUploadOpen(true);
          }
        },
        disabled: isSaving
      });
      
    }

    return actions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredProperties.length,
    handleEditClick,
    handleExportClick,
    isEditing,
    isSaving,
    propertyData.length
  ]);

  return (
    <div
      className="w-full relative flex flex-col bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
      style={containerStyle}
    >
      {/* 로딩 표시 */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
            <span className="text-sm text-gray-700">매물 데이터를 불러오는 중...</span>
          </div>
        </div>
      )}

      {/* 에러 표시 */}
      {error && !isLoading && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-50 border border-red-200 px-4 py-2 rounded-lg shadow-lg z-10">
          <span className="text-red-600 text-sm">⚠️ {error}</span>
        </div>
      )}

      {/* 좌표 동기화 진행 상황 */}

      {/* 선택 삭제 바 (편집 모드에서 선택된 항목이 있을 때) */}
      {isEditing && selectedForDelete.size > 0 && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between">
          <span className="text-sm text-red-700 font-medium">
            {selectedForDelete.size}개 선택됨
          </span>
          <button
            onClick={handleDeleteSelected}
            disabled={isDeleting}
            className="px-3 py-1.5 bg-red-500 text-white text-sm font-semibold rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                삭제 중...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                선택 삭제
              </>
            )}
          </button>
        </div>
      )}

      {/* 테이블 컨테이너 - 개선된 스크롤 */}
      <div className="flex-1 overflow-x-auto overflow-y-auto min-h-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e0 #f7fafc' }}>
        <style>{`
          /* 스크롤바 스타일링 (Chrome, Safari, Edge) */
          div::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          div::-webkit-scrollbar-track {
            background: #f7fafc;
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
          }
        `}</style>
        <table className="w-full border-collapse" style={{ minWidth: 'max-content' }}>
          {/* 테이블 헤더 */}
          <thead className="bg-orange-50 sticky top-0 z-5">
            <tr>
              {/* 편집 모드일 때 체크박스 컬럼 */}
              {isEditing && (
                <th className="border border-gray-300 px-1.5 md:px-3 py-1.5 md:py-2 text-center w-10">
                  <input
                    type="checkbox"
                    checked={filteredProperties.length > 0 && selectedForDelete.size === filteredProperties.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                    title="전체 선택"
                  />
                </th>
              )}
              {/* 번호 컬럼 */}
              <th className="border border-gray-300 px-1.5 md:px-3 py-1.5 md:py-2 text-center text-[0.875rem] md:text-sm font-semibold text-gray-700 whitespace-nowrap w-12">
                번호
              </th>
              {PROPERTY_COLUMNS.map((column) => {
                const columnWidth = columnWidths[column.key];
                const isResizing = resizingColumn === column.key;
                
                return (
                  <th 
                    key={column.key} 
                    className={`border border-gray-300 px-1.5 md:px-3 py-1.5 md:py-2 text-left text-[0.875rem] md:text-sm font-semibold text-gray-700 whitespace-nowrap relative ${
                      isResizing ? 'bg-orange-100' : ''
                    }`}
                    style={{ 
                      width: columnWidth ? `${columnWidth}px` : 'auto',
                      minWidth: columnWidth ? `${columnWidth}px` : 'auto',
                      maxWidth: columnWidth ? `${columnWidth}px` : 'none'
                    }}
                  >
                    <div className="flex items-center justify-between pr-1">
                      <span className="flex-1">{column.label}</span>
                      <div
                        className={`w-1 h-full cursor-col-resize hover:bg-orange-400 transition-colors flex-shrink-0 ${
                          isResizing ? 'bg-orange-500' : 'bg-gray-300'
                        }`}
                        onMouseDown={(e) => handleResizeStart(e, column.key)}
                        style={{ touchAction: 'none' }}
                        title="드래그하여 너비 조절"
                      />
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* 테이블 본문 */}
          <tbody>
            {displayedProperties.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={PROPERTY_COLUMNS.length + 1 + (isEditing ? 1 : 0)} className="border border-gray-300 px-4 py-8 text-center text-xs md:text-sm text-gray-500">
                  {filteredProperties.length === 0 ? '매물 데이터가 없습니다.' : '표시할 매물이 없습니다.'}
                </td>
              </tr>
            ) : (
              displayedProperties.map((property, index) => (
                <tr
                  key={property.id}
                  onClick={(e) => handleRowClick(property, e)}
                  onMouseDown={handleRowMouseDown}
                  onMouseMove={handleRowMouseMove}
                  onMouseUp={handleRowMouseUp}
                  onMouseLeave={handleRowMouseUp}
                  className={`transition-colors ${isEditing ? 'hover:bg-orange-100' : 'hover:bg-orange-50 cursor-pointer'} ${selectedForDelete.has(property.id) ? 'bg-red-50' : ''}`}
                  data-property-id={property.id}
                >
                  {/* 편집 모드일 때 체크박스 */}
                  {isEditing && (
                    <td className="border border-gray-300 px-1.5 md:px-3 py-1.5 md:py-2 text-center w-10">
                      <input
                        type="checkbox"
                        checked={selectedForDelete.has(property.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCheckboxToggle(property.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                      />
                    </td>
                  )}
                  {/* 번호 컬럼 */}
                  <td className="border border-gray-300 px-1.5 md:px-3 py-1.5 md:py-2 text-center text-xs md:text-sm text-gray-700 font-medium w-12">
                    {property.id}
                  </td>
                  {PROPERTY_COLUMNS.map((column) => {
                    const propertyId = property.id.toString();
                    const hasChanges = editingData[propertyId]?.[column.key] !== undefined;
                    const is상호Column = column.key === '상호';
                    const columnWidth = columnWidths[column.key];
                    
                    return (
                      <td 
                        key={column.key} 
                        onClick={(e) => handleCellClick(e, property, column)}
                        className={`border border-gray-300 px-1.5 md:px-3 py-1.5 md:py-2 text-xs md:text-sm text-gray-800 ${
                          isEditing ? 'cursor-text hover:bg-orange-100' : ''
                        } ${hasChanges ? 'bg-yellow-50' : ''}`}
                        style={{ 
                          wordBreak: 'keep-all', 
                          overflowWrap: 'break-word',
                          width: columnWidth ? `${columnWidth}px` : 'auto',
                          minWidth: columnWidth ? `${columnWidth}px` : 'auto',
                          maxWidth: columnWidth ? `${columnWidth}px` : 'none'
                        }}
                      >
                        <div className="flex items-center gap-1">
                          <div className="truncate flex-1" title={String(getCellValue(property, column))}>
                            {getCellValue(property, column)}
                          </div>
                          {is상호Column && onAddInterestedProperty && !isEditing && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddInterestedProperty(property);
                              }}
                              className="flex-shrink-0 text-yellow-500 hover:text-yellow-600 transition-colors p-0.5"
                              title="관심 매물 등록"
                              aria-label="관심 매물 등록"
                            >
                              <svg 
                                className="w-4 h-4 md:w-5 md:h-5" 
                                fill="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* 무한 스크롤 로더 */}
        {hasMore && (
          <div 
            ref={loaderRef}
            className="py-4 text-center text-gray-500 text-sm"
          >
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
              <span>더 불러오는 중... ({displayedProperties.length} / {filteredProperties.length})</span>
            </div>
          </div>
        )}

        {/* 모든 데이터 로드 완료 표시 */}
        {!hasMore && filteredProperties.length > PAGE_SIZE && (
          <div className="py-3 text-center text-gray-400 text-xs">
            전체 {filteredProperties.length}개 매물 표시 완료
          </div>
        )}
      </div>

      {/* 사이드바 */}
      <PropertySidebar
        property={selectedProperty}
        isOpen={isSidebarOpen}
        onClose={handleSidebarReset}
        onFocusMap={onFocusMap}
        onAddInterestedProperty={onAddInterestedProperty}
        onDelete={async (propertyId) => {
          try {
            await deleteProperty(propertyId);
            await loadProperties();
            handleSidebarReset();
            alert('매물이 성공적으로 삭제되었습니다.');
          } catch (error) {
            console.error('매물 삭제 실패:', error);
            throw error;
          }
        }}
        onPropertyUpdate={loadProperties}
      />

      {/* 퀵 기능 버튼 - GUEST는 전체 숨김 */}
      {!isGuest && (
        <ButtonListFunction
          actions={quickActions}
          disabled={isSaving}
          isEditing={isEditing}
          onComplete={handleSaveClick}
        />
      )}

      {/* PDF 업로드/파싱 모달 */}
      {isPdfUploadOpen && (
        <PdfUploadTest
          onClose={() => setIsPdfUploadOpen(false)}
          onSuccess={loadProperties}
        />
      )}


      {/* 매물 추가 모달 */}
      <AddCard
        isOpen={isAddCardOpen}
        onClose={() => setIsAddCardOpen(false)}
        onSuccess={loadProperties}
        existingProperties={propertyData}
      />

      {/* 주소 검색 팝업 (편집 모드용) */}
      <SearchAddress
        isOpen={isAddressSearchOpen}
        onClose={() => {
          setIsAddressSearchOpen(false);
          setAddressEditTarget(null);
        }}
        onSelect={handleAddressSelect}
      />

      {/* DEMO 역할 기능 설명 모달 */}
      <DemoFeatureInfoModal
        isOpen={demoInfoModal.isOpen}
        onClose={() => setDemoInfoModal({ ...demoInfoModal, isOpen: false })}
        featureName={demoInfoModal.featureName}
        description={demoInfoModal.description}
        steps={demoInfoModal.steps}
      />

      {/* 엑셀 업로드/다운로드 모달 */}
      {isExcelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">엑셀</h2>
                <button
                  onClick={() => setIsExcelModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="닫기"
                  disabled={isExcelUploading}
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* 다운로드 버튼 */}
                <button
                  onClick={() => {
                    handleExportClick();
                    setIsExcelModalOpen(false);
                  }}
                  disabled={filteredProperties.length === 0 || isExcelUploading}
                  className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>엑셀 다운로드</span>
                </button>

                {/* 업로드 영역 */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    엑셀 업로드
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleExcelUpload(file);
                        }
                      }}
                      className="hidden"
                      id="excel-upload-input"
                      disabled={isExcelUploading}
                    />
                    <label
                      htmlFor="excel-upload-input"
                      className={`cursor-pointer flex flex-col items-center ${
                        isExcelUploading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-gray-600 font-medium">엑셀 파일 선택</span>
                      <span className="text-sm text-gray-400 mt-1">.xlsx, .xls 파일</span>
                    </label>
                  </div>

                  {/* 업로드 진행 상황 */}
                  {isExcelUploading && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">업로드 중...</span>
                        <span className="text-sm text-gray-600">
                          {excelUploadProgress.current} / {excelUploadProgress.total}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(excelUploadProgress.current / excelUploadProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  * 다운로드한 엑셀 파일 형식을 그대로 사용하여 업로드할 수 있습니다.
                  <br />
                  * 첫 번째 행은 헤더로 인식되며, 두 번째 행부터 데이터로 처리됩니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainList;

